import { OfferUI } from 'common/contracts';
import { EFeatureFlag, ESpecialOfferInternalViewModel } from 'constants/enums';
import { AnyObjectSchema } from 'yup';
import type { ProductData } from '@appcharge/shared-ui';

import { GradientDirection } from 'components/AcGradientInput/AcGraidentInput.types';
import { GeneralThemeFormValues } from 'components/Theme/GeneralSettings/generalTheme.types';
import { StoreThemeFormValues } from 'components/Theme/StoreTheme/storeTheme.types';

export type UniqueFlag = 'isNameUnique' | 'isExternalIdUnique';

export enum OfferDesignTypes {
  SpecialOffer = 'SpecialOffer',
  Bundle = 'Bundle',
  PopUp = 'PopUp',
  TriggeredPopup = 'TriggeredPopup',
  RollingOffer = 'RollingOffer',
  ProgressBar = 'ProgressBar'
}

export interface OfferDesignRow {
  _id: string;
  name: string;
  externalId: string;
  backgroundImage?: string;
  offerUiType: string;
  createdAt: string;
}

export interface OfferDesignActions {
  onEdit: (id: string, offerUiType: string) => void;
  onDuplicate: (id: string, offerUiType: string) => void;
  onDelete: (id: string) => void;
}

export interface OfferDesignPermissions {
  canUserEdit: boolean;
  canManage: boolean;
}

export interface FilterCriteria {
  externalId: string;
  name: string;
  types: string[];
}

export interface OfferTypeCardConfig {
  key: string;
  title: string;
  description: string;
  image: string;
  offerDesignType: OfferDesignTypes;
  disabled?: boolean;
  featureFlag?: EFeatureFlag;
  flagTitle?: string;
  flagTitleFlag?: EFeatureFlag;
}

export interface GradientColor {
  colorOne: string;
  colorTwo?: string;
  gradientDirection?: GradientDirection;
}

interface IBaseFormValues {
  name: string;
  externalId: string;
  backgroundImage: string;
  backgroundColor: GradientColor | string;
  buttonSuffixImage: string;
  buttonSuffixAnimation: string;
  borderColor: GradientColor;
  borderWidth: number;
  offerUiType: OfferDesignTypes;
}

interface IPromotionFormValues {
  collectText?: string;
  title?: string;
  fontSize?: number;
  fontWeight?: string;
  fontColor?: GradientColor;
  subTitleText?: string;
  subTitleFontSize?: number;
  subTitleFontWeight?: string;
  subTitleFontColor?: GradientColor;
}

interface IProgressBarFormValues {
  backgroundOpacity?: number;
  frameRadius?: number;
  barFillColor?: GradientColor;
  barBackgroundColor?: GradientColor;
  barRadius?: number;
  accumulatedItemImageUrl?: string;
  accumulatedItemBackgroundImageUrl?: string;
  textColor?: string;
  barBackgroundImage?: string;
  headerImage?: string;
}

interface IPopupFormValues {
  badgeCoverImage?: string;
  offerUiSubType?: string;
  products?: ProductData[];
}

interface IRollingOfferFormValues {
  arrowColor?: string;
  arrowImage?: string;
  enableScroll?: boolean;
  scrollArrowImage?: string;
  horizontalBackgroundImage?: string;
  offersBackgroundColor?: GradientColor;
  offersBackgroundImage?: string;
}

export type OfferDesignFormValues = IBaseFormValues
  & IPromotionFormValues
  & IProgressBarFormValues
  & IPopupFormValues
  & IRollingOfferFormValues;

export type InitialValuesBuilder = (
  data: OfferUI | undefined,
  offerSubType?: string
) => Partial<OfferDesignFormValues>;

export type RenderCardFn = (
  values: OfferDesignFormValues,
  generalTheme: GeneralThemeFormValues,
  storeScreen: StoreThemeFormValues
) => React.ReactNode;

export interface SaveValidationCheck {
  failed: boolean;
  message: string;
  description: string;
  fieldKey?: string;
}

export interface OfferTypeFormConfig {
  FormFields: React.FC<{
    shouldDisplaySubTitle?: boolean;
    specialOffersInternalViewModel?: ESpecialOfferInternalViewModel;
  }>;
  schema: AnyObjectSchema;
  buildPayload: (formValues: OfferDesignFormValues, offerType: OfferDesignTypes) => OfferUI;
}
